import React, { useEffect, useMemo, useState } from "react";

import { serverMessageOf } from "../apiError";
import { useCabinetRefresh } from "../CabinetRefresh";
import { CabinetEmpty, CabinetSkeleton } from "../components/CabinetStates";
import {
  averagePerDayOf,
  barHeightPercent,
  consumptionParamsOf,
  dailyTotalsOf,
  formatDay,
  isSingleDay,
  linkStartOf,
  localIsoDate,
  periodRangeOf,
  PeriodKind,
  PeriodRange,
} from "../consumptionView";
import { formatDate, formatNumber } from "../format";
import CabinetConsumptionApi from "../services/CabinetConsumptionApi";
import CabinetStationsApi from "../services/CabinetStationsApi";
import type { MyConsumption, MyConsumptionRow, MyPlace } from "../types";

/**
 * `Ф2` (кабинет ПЗС): потребление.
 *
 * `GET /my/consumption` — расход по своим местам: выбор места, период (день,
 * неделя, месяц, свои даты), итог, среднее в сутки, показания на границах,
 * столбцы по дням и таблица по 7 строк с «показать ещё». `Скачать CSV` зовёт
 * `/my/consumption.csv` с **теми же** параметрами.
 *
 * Период обрезан периодом связи с местом (`ДС-03`): запрос раньше начала связи
 * вернёт строки с её начала, а текст под таблицей назовёт эту дату.
 */

const PERIODS: Array<{ kind: PeriodKind; label: string }> = [
  { kind: "day", label: "День" },
  { kind: "week", label: "Неделя" },
  { kind: "month", label: "Месяц" },
  { kind: "custom", label: "Период…" },
];

function numberCell(value: number | null | undefined): string {
  return formatNumber(value, { maximumFractionDigits: 1 });
}

function ConsumptionTable({ rows }: { rows: MyConsumptionRow[] }) {
  const ordered = useMemo(
    () => [...rows].sort((left, right) => right.date.localeCompare(left.date)),
    [rows],
  );

  // «Показать ещё» запоминается вместе с набором строк: новый период снова
  // начинается с 7 строк, без эффекта, который мог бы сбросить счётчик после
  // нажатия.
  const rowsKey = `${rows.length}:${rows[0]?.date ?? ""}:${rows[rows.length - 1]?.date ?? ""}`;
  const [expansion, setExpansion] = useState({ key: rowsKey, count: 7 });
  const visible = expansion.key === rowsKey ? expansion.count : 7;

  const shown = ordered.slice(0, visible);

  return (
    <section className="cabinet-card" aria-labelledby="cabinet-rows-title">
      <div className="cabinet-card__head">
        <h2 id="cabinet-rows-title" className="cabinet-card__title">
          По дням
        </h2>
        <span className="cabinet-hint">
          Показаны последние {shown.length} строк · всего {ordered.length}
        </span>
      </div>

      <div className="cabinet-table-wrap">
        <table className="cabinet-table">
          <thead>
            <tr>
              <th>Дата</th>
              <th>Место</th>
              <th>На начало</th>
              <th>На конец</th>
              <th>Расход, кВт·ч</th>
              <th>Пометка</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((row, index) => (
              <tr
                key={`${row.date}-${row.place}-${row.meter_number}-${index}`}
                className={row.mark ? "cabinet-table__row--marked" : undefined}
              >
                <td className="cabinet-num">{formatDay(row.date)}</td>
                <td>{row.place ? `Место ${row.place}` : "—"}</td>
                <td className="cabinet-num">{numberCell(row.start_value)}</td>
                <td className="cabinet-num">{numberCell(row.end_value)}</td>
                <td className="cabinet-num cabinet-table__total">
                  {numberCell(row.consumption_kwh)}
                </td>
                <td>
                  {row.mark ? (
                    <span className="cabinet-mark">{row.mark}</span>
                  ) : (
                    "—"
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {visible < ordered.length ? (
        <div className="cabinet-form__actions cabinet-form__actions--center">
          <button
            type="button"
            className="cabinet-btn"
            onClick={() =>
              setExpansion({ key: rowsKey, count: visible + 7 })
            }
          >
            Показать ещё
          </button>
        </div>
      ) : null}
    </section>
  );
}

function ConsumptionChart({ rows }: { rows: MyConsumptionRow[] }) {
  const daily = useMemo(() => dailyTotalsOf(rows), [rows]);
  const max = daily.reduce((top, bar) => Math.max(top, bar.total), 0);

  if (daily.length === 0) {
    return (
      <section className="cabinet-card" aria-labelledby="cabinet-chart-title">
        <h2 id="cabinet-chart-title" className="cabinet-card__title">
          По дням
        </h2>
        <CabinetEmpty title="Нет данных за период" icon={false} />
      </section>
    );
  }

  return (
    <section className="cabinet-card" aria-labelledby="cabinet-chart-title">
      <div className="cabinet-card__head">
        <h2 id="cabinet-chart-title" className="cabinet-card__title">
          По дням
        </h2>
        <span className="cabinet-hint">
          Наведите на столбец — значение и дата
        </span>
      </div>

      <div className="cabinet-bars" role="img" aria-label="Расход по дням">
        {daily.map((bar) => (
          <div
            key={bar.date}
            className="cabinet-bars__bar"
            style={{ height: `${barHeightPercent(bar.total, max)}%` }}
            title={`${formatDay(bar.date)}: ${formatNumber(bar.total, {
              maximumFractionDigits: 1,
            })} кВт·ч`}
          />
        ))}
      </div>

      <div className="cabinet-bars__axis cabinet-num">
        <span>{formatDay(daily[0].date)}</span>
        <span>{formatDay(daily[daily.length - 1].date)}</span>
      </div>
    </section>
  );
}

/**
 * `F.7` (`oq-ММБ-215`, решение РП 22.10): итог за одни сутки.
 *
 * Столбец за один день — это одна полоса во всю высоту: по ней не видно ничего.
 * Поэтому за день показывается итог и показания счётчика на начало и конец —
 * ровно те числа, по которым расход и посчитан.
 */
function ConsumptionDayTotal({
  data,
  withReadings,
}: {
  data: MyConsumption;
  withReadings: boolean;
}) {
  return (
    <section className="cabinet-card" aria-labelledby="cabinet-day-total-title">
      <div className="cabinet-card__head">
        <h2 id="cabinet-day-total-title" className="cabinet-card__title">
          Итог за день
        </h2>
      </div>

      <div className="cabinet-metrics">
        <div className="cabinet-metric cabinet-metric--accent">
          <span className="cabinet-metric__label">Расход за день</span>
          <b className="cabinet-metric__value cabinet-num">
            {numberCell(data.total_kwh)} кВт·ч
          </b>
        </div>
        {/* `H.5` (`АРХ-ПАК-23.10`): при «Все места» показания дня не выводим —
            `start_value`/`end_value` берутся по краям строк, а у разных мест это
            разные счётчики, и вместе они означают ничего. */}
        {withReadings ? (
          <>
            <div className="cabinet-metric">
              <span className="cabinet-metric__label">Показание на начало дня</span>
              <b className="cabinet-metric__value cabinet-num">
                {numberCell(data.start_value)}
              </b>
            </div>
            <div className="cabinet-metric">
              <span className="cabinet-metric__label">Показание на конец дня</span>
              <b className="cabinet-metric__value cabinet-num">
                {numberCell(data.end_value)}
              </b>
            </div>
          </>
        ) : null}
      </div>
    </section>
  );
}

export default function CabinetConsumptionPage() {
  const { version, refresh, markUpdated, markFailed } = useCabinetRefresh();
  const today = useMemo(() => localIsoDate(), []);

  const [kind, setKind] = useState<PeriodKind>("week");
  const [range, setRange] = useState<PeriodRange>(() =>
    periodRangeOf("week", today),
  );
  const [custom, setCustom] = useState<PeriodRange>(() =>
    periodRangeOf("week", today),
  );
  const [placeId, setPlaceId] = useState<number | null>(null);
  const [places, setPlaces] = useState<MyPlace[]>([]);
  const [data, setData] = useState<MyConsumption | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // `G.1`: состояние и функция выгрузки убраны вместе с кнопкой — держать
  // неиспользуемый код ради «вдруг вернут» дороже, чем вернуть его из истории.

  const params = useMemo(
    () => consumptionParamsOf({ placeId, range, step: "day" }),
    [placeId, range],
  );

  // `F.7`: за одни сутки столбец один и ничего не показывает — вместо столбцов
  // итог за день и показания на границах.
  const singleDay = isSingleDay(range);

  // Места — для выбора и для текста «данные доступны с …».
  useEffect(() => {
    let alive = true;

    CabinetStationsApi.getMyPlaces()
      .then((next) => {
        if (alive) setPlaces(next);
      })
      .catch(() => {
        // Список мест не обязателен для расчёта: остаётся «Все места».
      });

    return () => {
      alive = false;
    };
  }, [version]);

  useEffect(() => {
    let alive = true;

    setLoading(true);

    CabinetConsumptionApi.getConsumption(params)
      .then((answer) => {
        if (!alive) return;
        setData(answer);
        setError(null);
        markUpdated();
      })
      .catch((requestError) => {
        if (!alive) return;
        markFailed();
        setError(
          serverMessageOf(
            requestError,
            "Не удалось загрузить расход. Попробуйте ещё раз.",
          ),
        );
      })
      .finally(() => {
        if (alive) setLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [params, version, markUpdated, markFailed]);

  const pickPeriod = (next: PeriodKind) => {
    setKind(next);

    if (next !== "custom") {
      setRange(periodRangeOf(next, today));
    }
  };

  const applyCustom = () => {
    setKind("custom");
    setRange({ ...custom });
  };

  const average = data ? averagePerDayOf(data.total_kwh, range) : 0;
  const linkStart = linkStartOf(places, placeId);

  return (
    <section className="cabinet-page" aria-labelledby="cabinet-consumption-title">
      <header className="cabinet-page__head cabinet-page__head--row">
        <div>
          <h1 id="cabinet-consumption-title">Потребление</h1>
          <p>Расход по показаниям счётчика станции, кВт·ч</p>
        </div>
        {/* `G.1` (`oq-ММБ-221`, решение РП 22.10): кнопки «Скачать CSV» в кабинете
            больше нет — ни на широком экране, ни на мобильном. Серверная выгрузка
            `GET /my/consumption.csv` остаётся: ею пользуются проверки и поддержка,
            а `getCsv` в API оставлен, чтобы её не пришлось возвращать обратно. */}
      </header>

      <div className="cabinet-filters">
        <label className="cabinet-field">
          <span>Машиноместо</span>
          <select
            aria-label="Машиноместо"
            value={placeId ?? ""}
            onChange={(event) =>
              setPlaceId(event.target.value ? Number(event.target.value) : null)
            }
          >
            <option value="">Все места</option>
            {places.map((place) => (
              <option key={place.link_id} value={place.place?.id ?? ""}>
                Место {place.place?.number ?? "—"}
                {place.state === "closed" ? " (связь закрыта)" : ""}
              </option>
            ))}
          </select>
        </label>

        <div className="cabinet-field">
          <span>Период</span>
          <div className="cabinet-segments" role="group" aria-label="Период">
            {PERIODS.map((period) => (
              <button
                key={period.kind}
                type="button"
                className={`cabinet-segment${kind === period.kind ? " cabinet-segment--active" : ""}`}
                aria-pressed={kind === period.kind}
                onClick={() => pickPeriod(period.kind)}
              >
                {period.label}
              </button>
            ))}
          </div>
        </div>

        {kind === "custom" ? (
          <div className="cabinet-custom-dates">
            <label className="cabinet-field">
              <span>С даты</span>
              <input
                type="date"
                value={custom.from}
                onChange={(event) =>
                  setCustom((current) => ({ ...current, from: event.target.value }))
                }
              />
            </label>
            <label className="cabinet-field">
              <span>По дату</span>
              <input
                type="date"
                value={custom.to}
                onChange={(event) =>
                  setCustom((current) => ({ ...current, to: event.target.value }))
                }
              />
            </label>
            <button type="button" className="cabinet-btn" onClick={applyCustom}>
              Применить
            </button>
          </div>
        ) : null}

        <div className="cabinet-field">
          <span>Даты</span>
          <b className="cabinet-num cabinet-filters__range">
            {formatDay(range.from)} — {formatDay(range.to)}
          </b>
        </div>
      </div>

      {error ? (
        <p className="cabinet-notice cabinet-notice--error" role="status">
          {error}
        </p>
      ) : null}
      {loading && !data ? <CabinetSkeleton /> : null}

      {!loading && error && !data ? (
        <CabinetEmpty title="Не удалось загрузить расход">
          <button type="button" className="cabinet-btn" onClick={refresh}>
            Обновить
          </button>
        </CabinetEmpty>
      ) : null}

      {data ? (
        <>
          <div className="cabinet-metrics">
            <div className="cabinet-metric cabinet-metric--accent">
              <span className="cabinet-metric__label">Расход за период</span>
              <b className="cabinet-metric__value cabinet-num">
                {numberCell(data.total_kwh)} кВт·ч
              </b>
            </div>
            {/* `H.5` (решение РП по `F.7`): за одни сутки «в среднем за сутки» — это
                тот же итог, повторённый рядом. За день её не выводим. */}
            {singleDay ? null : (
              <div className="cabinet-metric">
                <span className="cabinet-metric__label">В среднем за сутки</span>
                <b className="cabinet-metric__value cabinet-num">
                  {numberCell(average)} кВт·ч
                </b>
              </div>
            )}
            <div className="cabinet-metric">
              <span className="cabinet-metric__label">Показание на начало</span>
              <b className="cabinet-metric__value cabinet-num">
                {numberCell(data.start_value)}
              </b>
            </div>
            <div className="cabinet-metric">
              <span className="cabinet-metric__label">Показание на конец</span>
              <b className="cabinet-metric__value cabinet-num">
                {numberCell(data.end_value)}
              </b>
            </div>
          </div>

          {data.rows.length > 0 ? (
            <>
              {/* `F.7`: за одни сутки столбцы не показываем — вместо них итог дня. */}
              {singleDay ? (
                <ConsumptionDayTotal data={data} withReadings={placeId !== null} />
              ) : (
                <ConsumptionChart rows={data.rows} />
              )}
              <ConsumptionTable rows={data.rows} />
            </>
          ) : (
            <CabinetEmpty title="Пока нет данных">
              Расход появится после первых показаний счётчика за выбранный
              период.
            </CabinetEmpty>
          )}
        </>
      ) : null}

      {linkStart ? (
        <p className="cabinet-hint cabinet-consumption__note">
          Данные доступны за период вашей связи с местом — с{" "}
          {formatDate(linkStart)}. Расход считается как разница показаний
          счётчика на начало и конец дня; при перерыве связи расход не теряется,
          а попадает в день восстановления связи.
        </p>
      ) : null}
    </section>
  );
}
